import clsx from 'clsx';
import { Logo } from '@/components/viz/Logo';
import { ArcanumMarkdown } from '@/components/ui/ArcanumMarkdown';
import type { ProvenanceRecord, ModelSchema } from '@arcanum-hq/shared-types';

interface MessageBubbleProps {
  role: 'human' | 'ai';
  content: string;
  initials?: string;
  /**
   * Hydration target for `?messageId=` MutationObserver scroll handler
   * (I1 from design.md — Phase 6 deep-link consumer plants the listener;
   * Phase 2 Session 4 plants the attribute hook).
   */
  dataMessageId?: string;
  /**
   * Provenance map for the active conversation, refined from
   * `agentState.inputProvenance` at the route boundary (Phase 2 Session 5).
   * Threaded down through `<ChatThread>`. `null` while loading.
   */
  inputProvenance?: Record<string, ProvenanceRecord> | null;
  /**
   * Active model schema (refined at the route boundary). Threaded down
   * through `<ChatThread>`. `null` while loading; consumed by `<DeepLink>`
   * for I6 pre-validation.
   */
  modelSchema?: ModelSchema | null;
}

/** Strip raw JSON blobs (LangChain ToolMessages, tool results) from AI message content */
function cleanContent(text: string): string {
  const lines = text.split('\n');
  const cleanLines: string[] = [];
  for (const line of lines) {
    const trimmed = line.trim();
    if (
      trimmed.startsWith('{') &&
      (trimmed.includes('"lc"') ||
        trimmed.includes('"kwargs"') ||
        trimmed.includes('"tool_call_id"') ||
        trimmed.includes('"summary"') ||
        (trimmed.includes('"status"') && trimmed.includes('"content"')))
    )
      break;
    if (
      trimmed.startsWith('"lc"') ||
      trimmed.startsWith('"type": "constructor"')
    )
      break;
    cleanLines.push(line);
  }
  return cleanLines.join('\n').trim();
}

export function MessageBubble({
  role,
  content,
  initials = 'AA',
  dataMessageId,
  inputProvenance = null,
  modelSchema = null,
}: MessageBubbleProps) {
  const isAi = role === 'ai';
  const displayContent = isAi ? cleanContent(content) : content;

  if (isAi && !displayContent) return null;

  return (
    <div
      data-testid="message-bubble"
      data-role={role}
      data-message-id={dataMessageId}
      className={clsx(
        'flex gap-2.5 items-start',
        isAi ? 'justify-start' : 'justify-end'
      )}
    >
      {isAi && (
        <div className="w-6 h-6 rounded-[3px] shrink-0 bg-accent-dim border border-accent/10 flex items-center justify-center mt-0.5">
          <Logo size={10} />
        </div>
      )}

      <div
        className={clsx(
          'max-w-[75%] px-4 py-3 rounded-[4px] border',
          isAi
            ? 'bg-panel-bg border-panel-border'
            : 'bg-accent-dim border-accent/9'
        )}
      >
        <ArcanumMarkdown
          content={displayContent}
          density="default"
          inputProvenance={inputProvenance}
          modelSchema={modelSchema}
        />
      </div>

      {!isAi && (
        <div className="w-6 h-6 rounded-[3px] shrink-0 bg-input-bg border border-panel-border flex items-center justify-center mt-0.5 text-xs text-text-dim font-mono font-semibold">
          {initials}
        </div>
      )}
    </div>
  );
}
